import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router';
import IMAGES from '../../assets/images';
import CustomImage from '../../components/CustomImage';
import { COMPANY } from '../../constants/company.constants';
import useScreenSize from '../../hooks/useScreenSize';
import useSectionNav, { HOME_SECTIONS } from '../../hooks/useSectionNav';
import { Facebook, Linkedin, Youtube } from '../../icons';
import { PATHS } from '../../routes/paths';

// `section` links scroll to a home page section; the rest open a page.
type FooterLink = {
  title: string;
  href?: string;
  section?: string;
  external?: boolean;
};

const productLinks: FooterLink[] = [
  ...HOME_SECTIONS.map(({ id, label }) => ({ title: label, section: id })),
  { title: 'Job Portal', href: PATHS.jobPortal.root },
];

// Upscale BD Ltd. pages — HRx is one of its products.
const companyLinks: FooterLink[] = [
  { title: 'About Us', href: 'https://upscalebd.com/about-us', external: true },
  { title: 'Our Team', href: 'https://upscalebd.com/our-team', external: true },
  {
    title: 'Case Studies',
    href: 'https://upscalebd.com/case-studies',
    external: true,
  },
  { title: 'Insights', href: 'https://upscalebd.com/insights', external: true },
  { title: 'Careers', href: 'https://upscalebd.com/career', external: true },
];

const supportLinks: FooterLink[] = [
  { title: 'Contact Us', href: '/contact' },
  { title: 'Privacy Policy', href: '/privacy-policy' },
  { title: 'Refund Policy', href: '/refund-policy' },
  // { title: 'Terms & Conditions', href: 'https://upscalebd.com/terms-and-conditions' },
];

const linkGroups = [
  { title: 'Product', links: productLinks },
  { title: 'Company', links: companyLinks },
  { title: 'Support', links: supportLinks },
];

const socialLinks = [
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/profile.php?id=61576860683385',
    icon: Facebook,
  },
  {
    label: 'YouTube',
    href: 'https://www.youtube.com/@UpscaleBD',
    icon: Youtube,
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/upscalebd-ltd',
    icon: Linkedin,
  },
];

const linkClass =
  'group inline-flex items-center gap-1 text-sm text-gray-500 transition-colors hover:text-gray-900';

const Footer = () => {
  const isSmScreen = useScreenSize('sm');
  const isMdScreen = useScreenSize('md');
  const goToSection = useSectionNav();

  return (
    <footer className="relative overflow-hidden border-t border-gray-200 bg-gray-50/60">
      <div className="pointer-events-none absolute -bottom-40 -left-32 h-[320px] w-[520px] rounded-full bg-theme-primary/[0.07] blur-[120px]" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 py-16 lg:grid-cols-12 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-5">
            <Link to="/" aria-label="HRx home" className="inline-block">
              <CustomImage
                src={IMAGES.hrxLogo}
                width={76}
                height={40}
                alt="HRx Logo"
              />
            </Link>
            <p className="mt-5 max-w-sm text-[15px] leading-7 text-gray-500">
              AI-powered, end-to-end HRM software that simplifies the entire
              employee lifecycle, from hiring and attendance to payroll and
              performance.
            </p>

            <a
              href={COMPANY.websiteUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white py-1 pl-1 pr-3 text-xs text-gray-500 transition-colors hover:border-gray-300 hover:text-gray-900"
            >
              <span className="rounded-full bg-theme-primary/10 px-2 py-0.5 font-medium text-theme-primary">
                A product of
              </span>
              <span className="font-medium text-gray-700">{COMPANY.name}</span>
              <ArrowUpRight className="h-3.5 w-3.5" />
            </a>

            <div className="mt-8 flex gap-2">
              {socialLinks.map(({ label, href, icon: Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-base text-gray-500 transition-colors hover:border-theme-primary/40 hover:text-theme-primary"
                >
                  <Icon />
                </a>
              ))}
            </div>
          </div>

          {/* Link groups */}
          <div className="grid grid-cols-2 gap-x-8 gap-y-10 sm:grid-cols-3 lg:col-span-7">
            {linkGroups.map((group) => (
              <div key={group.title}>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-900">
                  {group.title}
                </h3>
                <ul className="mt-5 space-y-3.5">
                  {group.links.map((link) => (
                    <li key={link.title}>
                      {link.section ? (
                        <button
                          onClick={() => goToSection(link.section!)}
                          className={linkClass}
                        >
                          {link.title}
                        </button>
                      ) : link.external ? (
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noreferrer"
                          className={linkClass}
                        >
                          {link.title}
                          <ArrowUpRight className="h-3.5 w-3.5 text-gray-300 transition-colors group-hover:text-theme-primary" />
                        </a>
                      ) : (
                        <Link to={link.href!} className={linkClass}>
                          {link.title}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Payment methods */}
        <div className="border-t border-gray-200 py-3">
          <img
            src={
              isSmScreen
                ? IMAGES.paymentMobile
                : isMdScreen
                  ? IMAGES.paymentTab
                  : IMAGES.paymentDesktop
            }
            alt="Supported payment methods"
            className="w-full mix-blend-multiply"
          />
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col gap-2 border-t border-gray-200 py-6 text-xs text-gray-400 md:flex-row md:items-center md:justify-between">
          <p>
            © {new Date().getFullYear()} {COMPANY.product} by {COMPANY.name} All
            rights reserved.
          </p>
          <p className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
            <span>{COMPANY.address}</span>
            <span
              aria-hidden
              className="hidden h-3 w-px bg-gray-300 md:block"
            />
            <span>Trade License: {COMPANY.tradeLicense}</span>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
